import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AreaChart, Area, ResponsiveContainer, Tooltip, XAxis } from "recharts";
import { Radio, Users, Activity, UserPlus, ShoppingBag, DollarSign, Plus, Play, Trophy } from "lucide-react";
import { toast } from "sonner";
import AppLayout from "@/components/AppLayout";
import api from "@/lib/api";
import { useLang } from "@/i18n";

const rm = (n) => "RM " + (n || 0).toLocaleString("en-MY");

function Stat({ icon: Icon, label, value, accent, testid }) {
  return (
    <div data-testid={testid} className="rounded-xl border border-border bg-card p-5">
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground font-medium">{label}</span>
        <Icon className={`h-4 w-4 ${accent}`} />
      </div>
      <div className="mt-3 text-2xl font-mono font-bold tracking-tight">{value}</div>
    </div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    try { const res = await api.get("/dashboard"); setData(res.data); } catch (e) { toast.error("Failed to load"); }
  };
  useEffect(() => { load(); }, []);

  const newStudio = async () => {
    setCreating(true);
    try {
      const res = await api.post("/sessions", { title: "Untitled Live " + new Date().toLocaleDateString(), type: "Live Shopping", host: "Me" });
      navigate(`/studio/${res.data.id}`);
    } catch (e) { toast.error("Could not create studio"); } finally { setCreating(false); }
  };

  if (!data) return <AppLayout active="command"><div className="animate-pulse text-muted-foreground font-mono">Loading command center…</div></AppLayout>;

  return (
    <AppLayout active="command">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight">YES GO LIVE COMMAND</h1>
          <p className="text-sm text-muted-foreground mt-1">Go Live. Engage. Convert.</p>
        </div>
        <button data-testid="new-studio-btn" onClick={newStudio} disabled={creating} className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-[#E5253A] hover:bg-[#c71f31] text-white text-sm font-bold disabled:opacity-60">
          <Plus className="h-4 w-4" /> {t("new_studio")}
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
        <Stat testid="stat-live-now" icon={Radio} label={t("live_now")} value={data.live_now} accent="text-[#E5253A]" />
        <Stat testid="stat-viewers" icon={Users} label={t("total_viewers")} value={data.total_viewers.toLocaleString()} accent="text-cyan-400" />
        <Stat testid="stat-engagement" icon={Activity} label={t("engagement")} value={data.engagement + "%"} accent="text-emerald-400" />
        <Stat testid="stat-leads" icon={UserPlus} label={t("leads")} value={data.leads} accent="text-amber-400" />
        <Stat testid="stat-sales" icon={ShoppingBag} label={t("sales")} value={data.sales} accent="text-cyan-400" />
        <Stat testid="stat-revenue" icon={DollarSign} label={t("revenue")} value={rm(data.revenue)} accent="text-emerald-400" />
      </div>

      <div className="grid lg:grid-cols-3 gap-4 sm:gap-6 mt-6">
        <div className="lg:col-span-2 rounded-xl border border-border bg-card p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold tracking-tight">{t("revenue_7d")}</h3>
            <span className="text-xs font-mono text-emerald-400">▲ 24.8%</span>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={data.revenue_series}>
              <defs>
                <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#E5253A" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="#E5253A" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="day" stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} formatter={(v) => rm(v)} />
              <Area type="monotone" dataKey="revenue" stroke="#E5253A" strokeWidth={2} fill="url(#rev)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="rounded-xl border border-border bg-card p-5">
          <h3 className="font-bold tracking-tight flex items-center gap-2 mb-4"><Trophy className="h-4 w-4 text-amber-400" /> {t("host_performance")}</h3>
          <div className="space-y-3">
            {data.host_performance.map((h, i) => (
              <div key={h.host} data-testid={`host-row-${i}`} className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className={`h-6 w-6 rounded-full flex items-center justify-center text-xs font-bold font-mono ${i === 0 ? "bg-amber-500/20 text-amber-400" : "bg-accent text-muted-foreground"}`}>{i + 1}</span>
                  <div>
                    <div className="text-sm font-medium">{h.host}</div>
                    <div className="text-[10px] text-muted-foreground font-mono">{h.leads} leads · {h.sales} sales</div>
                  </div>
                </div>
                <span className="text-sm font-mono font-bold text-emerald-400">{rm(h.revenue)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-border bg-card p-5 mt-6">
        <h3 className="font-bold tracking-tight mb-4">{t("active_sessions")}</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.sessions.map((s) => (
            <div key={s.id} data-testid={`session-card-${s.id}`} className="rounded-lg border border-border p-4 hover:border-[#E5253A]/40 transition-colors">
              <div className="flex items-center justify-between">
                <span className={`text-[10px] font-mono px-2 py-0.5 rounded-full ${s.status === "live" ? "bg-[#E5253A]/15 text-[#E5253A]" : s.status === "scheduled" ? "bg-cyan-500/15 text-cyan-400" : "bg-accent text-muted-foreground"}`}>
                  {s.status === "live" && <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#E5253A] live-dot mr-1 align-middle" />}{s.status.toUpperCase()}
                </span>
                <span className="text-[10px] text-muted-foreground">{s.type}</span>
              </div>
              <div className="mt-2 font-semibold text-sm truncate">{s.title}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{s.host}</div>
              <div className="flex items-center gap-3 mt-3 text-[11px] font-mono text-muted-foreground">
                <span>{s.viewers.toLocaleString()} 👁</span>
                <span className="text-emerald-400">{rm(s.revenue)}</span>
              </div>
              <button data-testid={`open-studio-${s.id}`} onClick={() => navigate(`/studio/${s.id}`)} className="mt-3 w-full inline-flex items-center justify-center gap-2 h-9 rounded-lg border border-border hover:bg-accent text-sm font-medium">
                <Play className="h-3.5 w-3.5" /> {t("open_studio")}
              </button>
            </div>
          ))}
        </div>
      </div>
    </AppLayout>
  );
}
